import React from 'react';
import { Box, Spinner, Text, VStack } from '@chakra-ui/react';
import { getCachedData } from '../utils/apiCache';

/**
 * Smart loading fallback that only shows loading when data is not cached
 * Prevents unnecessary loading states when data is instantly available
 */
const SmartLoadingFallback = ({ 
  message = "Loading...", 
  cacheKey = null,
  children = null 
}) => {
  // If we have a cache key, check if data exists
  if (cacheKey) {
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      // Data is cached, don't show loading - render children or nothing
      return children || null;
    }
  }

  // No cached data, show loading state
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minH="200px"
      w="100%"
    >
      <VStack spacing={4}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="lg"
        />
        <Text color="gray.600" fontSize="sm">
          {message}
        </Text>
      </VStack>
    </Box>
  );
};

// Specific smart loading components for different sections
export const SmartDashboardLoading = () => (
  <SmartLoadingFallback 
    message="Loading Dashboard..." 
    cacheKey="/api/dashboard"
  />
);

export const SmartProfileLoading = () => (
  <SmartLoadingFallback 
    message="Loading Profile..." 
    cacheKey="/api/profile/get"
  />
);

export const SmartApplicationsLoading = () => (
  <SmartLoadingFallback 
    message="Loading Applications..." 
    cacheKey="/api/applications/get"
  />
);

export const SmartWorkHistoryLoading = () => (
  <SmartLoadingFallback 
    message="Loading Work History..." 
    cacheKey="/api/work-history/get"
  />
);

export const SmartEditorLoading = () => (
  <SmartLoadingFallback message="Loading Editor..." />
);

export const SmartAdminLoading = () => (
  <SmartLoadingFallback message="Loading Admin Panel..." />
);

export default SmartLoadingFallback;